/**
 * Labstep
 */

import { EntitySearchContainer as EntitySearchCursor } from 'labstep-web/components/Entity/Search/Cursor';
import { EntityTableParamsContainer as EntityTable } from 'labstep-web/components/Entity/Table';
import { LayoutLinksState } from 'labstep-web/components/Layout/Links/State';
import { LogList } from 'labstep-web/components/Log/List';
import { MetadataManagerContainer as MetadataManager } from 'labstep-web/components/Metadata/Manager';
import { ResearchAreaManager } from 'labstep-web/components/ResearchArea/Manager';
import { UserFormShowEditBiography } from 'labstep-web/components/User/Form/ShowEdit/Biography';
import { UserOrganizationWorkspacesManager } from 'labstep-web/components/UserOrganization/Action/ManageWorkspaces';
import { ICONS } from 'labstep-web/constants/icons';
import { CardEmptyState } from 'labstep-web/core/Card/EmptyState';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { Device } from 'labstep-web/models/device.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { Folder } from 'labstep-web/models/folder.model';
import { OrderRequest } from 'labstep-web/models/order-request.model';
import { Organization } from 'labstep-web/models/organization.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type { User } from 'labstep-web/models/user.model';
import type { SemanticICONS } from 'semantic-ui-react';

export const getBasicLinks = (user: User) => {
  let links = [
    {
      key: 'Bio',
      render: () => <UserFormShowEditBiography user={user} />,
    },
    {
      key: 'Research Areas',

      render: () => <ResearchAreaManager user={user} />,
    },
    {
      key: 'Info',
      render: () => (
        <MetadataManager
          entity={user}
          emptyStateMessage="Attach any other relevant information about yourself here."
        />
      ),
    },
  ];

  if (!user.group) {
    links = [links[0]];
  }

  return links;
};

export const getFullLinks = (
  user: User,
  organization: Organization,
) => {
  // Create links that match the StateCardLink interface (no routes needed)
  const links = [
    {
      children: 'Info',
      icon: 'info circle' as SemanticICONS,
      render: () => <LayoutLinksState tabs={getBasicLinks(user)} />,
    },
    {
      children: 'Workspaces',
      icon: ICONS.group.primary,
      render: () => (
        <UserOrganizationWorkspacesManager
          user={user}
          organization={organization}
        />
      ),
    },
    {
      children: 'Activity',
      icon: 'history' as SemanticICONS,
      render: () => (
        <ReadOnMountHOC
          key="activity"
          type="cursor"
          entityName="log"
          params={{
            author_id: user.id,
            count: 10,
            has_request_trace: true,
            action_not: 'security',
          }}
          children={({ entities }) => (
            <LogList
              logs={entities}
              entityName={Organization.entityName}
            />
          )}
        />
      ),
    },
  ];

  return links;
};

export const getGroupLinks = (user: User) => {
  const createEntityTableWithTabs = (
    entityName: string,
    params: Record<string, unknown>,
  ) => {
    return () => (
      <LayoutLinksState
        key="created_and_assigned_tabs"
        tabs={[
          {
            key: 'Created',
            render: () => (
              <EntitySearchCursor
                entityName={entityName}
                params={{ ...params, author_id: user.id }}
              >
                {({ entities, status }) => (
                  <EntityTable entities={entities} status={status} />
                )}
              </EntitySearchCursor>
            ),
          },
          {
            key: 'Assigned to',
            render: () => (
              <EntitySearchCursor
                entityName={entityName}
                params={{ ...params, assignee_id: user.id }}
                noResultsMessage={
                  <CardEmptyState
                    src="/img/empty-state/warning.svg"
                    title="No entities assigned"
                  />
                }
              >
                {({ entities, status }) => (
                  <EntityTable entities={entities} status={status} />
                )}
              </EntitySearchCursor>
            ),
          },
        ]}
      />
    );
  };

  return [
    {
      label: 'Notebook',
      links: [
        {
          children: 'Experiments',
          icon: ICONS.experiment_workflow.primary,
          render: createEntityTableWithTabs(
            ExperimentWorkflow.entityName,
            {},
          ),
        },
        {
          children: 'Templates',
          icon: ICONS.experiment_workflow.template,
          render: createEntityTableWithTabs(
            ExperimentWorkflow.entityName,
            {
              is_template: 1,
            },
          ),
        },
        {
          children: 'Collections',
          icon: ICONS.folder.primary,
          render: createEntityTableWithTabs(Folder.entityName, {
            type: 'experiment_workflow',
          }),
        },
      ],
    },
    {
      label: 'Protocol Library',
      links: [
        {
          children: 'Protocols',
          icon: ICONS.protocol_collection.primary,
          render: createEntityTableWithTabs(
            ProtocolCollection.entityName,
            {},
          ),
        },
        {
          children: 'Templates',
          icon: ICONS.experiment_workflow.template,
          render: createEntityTableWithTabs(
            ProtocolCollection.entityName,
            {
              is_template: 1,
            },
          ),
        },
        {
          children: 'Collections',
          icon: ICONS.folder.primary,
          render: createEntityTableWithTabs(Folder.entityName, {
            type: 'protocol_collection',
          }),
        },
      ],
    },
    {
      label: 'Inventory',
      links: [
        {
          children: 'Categories',
          icon: ICONS.resource_template.primary,
          render: createEntityTableWithTabs(Resource.entityName, {
            is_template: 1,
          }),
        },
        {
          children: 'Resources',
          icon: ICONS.resource.primary,
          render: createEntityTableWithTabs(Resource.entityName, {}),
        },
        {
          children: 'Items',
          icon: ICONS.resource_item.primary,
          render: createEntityTableWithTabs(
            ResourceItem.entityName,
            {},
          ),
        },
        {
          children: 'Order Requests',
          icon: ICONS.order_request.primary,
          render: createEntityTableWithTabs(
            OrderRequest.entityName,
            {},
          ),
        },
      ],
    },
    {
      label: 'Devices',
      links: [
        {
          children: 'Categories',
          icon: ICONS.device.template,
          render: createEntityTableWithTabs(Device.entityName, {
            is_template: 1,
          }),
        },
        {
          children: 'Devices',
          icon: ICONS.device.primary,
          render: createEntityTableWithTabs(Device.entityName, {
            group_id: user.group?.id,
          }),
        },
      ],
    },
  ];
};
